import {
  IconServer,
  IconRobot,
  IconBrandGithub,
  IconBrandAzure,
  IconBell,
  IconCalendar,
  IconShield,
  IconCode,
  IconRefresh,
  IconSatellite,
} from "@tabler/icons-react";
import { Link } from "@tanstack/react-router";
import { MoveRight } from "lucide-react";
import { lazy, Suspense, useState, useEffect } from "react";

import githubIssueExample from "@/assets/github-issue-example.png";
import logo from "@/assets/logo.png";
import { KubernetesLogo } from "@/assets/logos/kubernetes-logo";
import { SentryLogo } from "@/assets/logos/sentry-logo";
import { env } from "@/env/public-env";
import { FadeContent } from "@/shared/fade-content";
import { Button } from "@/shared/ui/button";
import { Card, CardContent } from "@/shared/ui/card";

const CyberneticGridShader = lazy(() =>
  import("@/shared/ui/cybernetic-grid-shader").then(
    (m) => ({ default: m.CyberneticGridShader })
  )
);

function getNextLaunchQuarter() {
  const now = new Date();
  const month = now.getMonth();
  const year = now.getFullYear();

  if (month < 3) return `Q2 ${year}`;
  if (month < 6) return `Q3 ${year}`;
  if (month < 9) return `Q4 ${year}`;
  return `Q1 ${year + 1}`;
}

function useIsDarkMode() {
  const [isDark, setIsDark] = useState(() =>
    typeof document !== "undefined"
      ? document.documentElement.classList.contains("dark")
      : false
  );

  useEffect(() => {
    const root = document.documentElement;
    const check = () =>
      setIsDark(root.classList.contains("dark"));
    check();

    const observer = new MutationObserver(check);
    observer.observe(root, {
      attributes: true,
      attributeFilter: ["class"],
    });
    return () => observer.disconnect();
  }, []);

  return isDark;
}

function HeroSection() {
  const isDark = useIsDarkMode();

  return (
    <section className="relative flex min-h-dvh flex-col items-center justify-center overflow-hidden bg-background">
      <Suspense fallback={null}>
        <CyberneticGridShader isDark={isDark} />
      </Suspense>
      <div
        className={
          /* oxlint-disable shadcn/no-arbitrary-values -- hero backdrop geometry is intentionally bespoke. */
          "absolute inset-0 z-1 bg-radial-[ellipse_50%_40%_at_center] from-background/80 via-background/40 to-transparent"
          /* oxlint-enable shadcn/no-arbitrary-values */
        }
      />
      <div className="relative z-10 container mx-auto max-w-6xl px-4 md:px-6">
        <div className="text-center">
          <img
            src={logo}
            alt="vivus logo"
            className="mx-auto mb-14 w-24 md:mb-18 md:w-28 lg:mb-20 lg:w-32"
          />
          <h1 className="text-5xl font-bold text-balance md:text-6xl lg:text-7xl">
            Stop firefighting production at 2AM
          </h1>
          <p className="mx-auto mt-14 max-w-3xl text-xl text-balance text-muted-foreground md:mt-16 md:text-2xl">
            Automatic detection of pod crashes, CI/CD
            failures, Sentry errors, and any OpenTelemetry
            signal. AI-ready issues in GitHub—let Claude fix
            them while you sleep.
          </p>

          {env.VITE_WAITLIST_MODE ? (
            <Button
              render={<Link to="/waitlist" />}
              className="mt-16 md:mt-20"
              size="lg"
            >
              Get Early Access
              <MoveRight className="ml-2" strokeWidth={1} />
            </Button>
          ) : (
            <Button
              render={<Link to="/app/dashboard" />}
              className="mt-16 md:mt-20"
              size="lg"
            >
              Get Started
              <MoveRight className="ml-2" strokeWidth={1} />
            </Button>
          )}

          <p className="mt-8 text-base text-muted-foreground md:mt-10">
            Free while in beta. No credit card required.
          </p>
        </div>
      </div>
    </section>
  );
}

function BeforeAfterSection() {
  const rows = [
    {
      before: "Manually checking pod logs at 2AM",
      after:
        "Automatic crash detection in under 60 seconds",
    },
    {
      before: "100 Slack alerts for the same pod crash",
      after: "1 GitHub issue per problem, deduplicated",
    },
    {
      before: "Pipeline failed 3 hours ago, nobody noticed",
      after: "GitHub issue created the minute it fails",
    },
    {
      before: "Sentry errors piling up, no owner assigned",
      after: "Auto-assigned to the right developer",
    },
    {
      before: "Copy-pasting logs into tickets manually",
      after:
        "Issues created with logs, context, and @claude mention",
    },
    {
      before:
        "Errors scattered across logs, traces, and dashboards",
      after:
        "Push any OpenTelemetry signal → GitHub issue in minutes",
    },
  ];

  return (
    <section className="border-t py-16">
      <div className="container mx-auto max-w-6xl px-4 md:px-6">
        <h2 className="mb-4 text-center text-2xl font-semibold">
          What changes when you use vivus
        </h2>
        <p className="mx-auto mb-12 max-w-2xl text-center text-muted-foreground">
          From reactive firefighting to proactive issue
          management.
        </p>

        <div className="mx-auto max-w-3xl">
          <div className="grid grid-cols-2 gap-0 overflow-hidden rounded-xl border">
            <div className="border-b bg-destructive/5 px-5 py-3 text-sm font-semibold text-destructive">
              Without vivus
            </div>
            <div className="border-b border-l bg-success/5 px-5 py-3 text-sm font-semibold text-success-foreground">
              With vivus
            </div>
            {rows.map((row) => (
              <div key={row.before} className="contents">
                <div className="border-b bg-destructive/5 px-5 py-3.5 text-sm text-muted-foreground last:border-b-0">
                  {row.before}
                </div>
                <div className="border-b border-l bg-success/5 px-5 py-3.5 text-sm last:border-b-0">
                  {row.after}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-10 text-center">
          {env.VITE_WAITLIST_MODE ? (
            <Button
              render={<Link to="/waitlist" />}
              size="lg"
              variant="outline"
            >
              Get Early Access
              <MoveRight className="ml-2" strokeWidth={1} />
            </Button>
          ) : (
            <Button
              render={<Link to="/app/dashboard" />}
              size="lg"
              variant="outline"
            >
              Get Started
              <MoveRight className="ml-2" strokeWidth={1} />
            </Button>
          )}
        </div>
      </div>
    </section>
  );
}

function GitHubIssueShowcaseSection() {
  return (
    <section className="border-t py-16">
      <div className="container mx-auto max-w-6xl px-4 md:px-6">
        <h2 className="mb-4 text-center text-2xl font-semibold">
          A real crash. A real GitHub issue. Created
          automatically.
        </h2>
        <p className="mx-auto mb-12 max-w-2xl text-center text-muted-foreground">
          A real GitHub issue, created automatically when a
          Kubernetes pod started crash-looping. Complete
          with logs, context, and an @claude mention for AI
          analysis.
        </p>

        <FadeContent delay={100} duration={800} blur>
          <div className="mx-auto max-w-3xl">
            <div className="overflow-hidden rounded-xl border bg-background shadow-xl shadow-black/5 dark:shadow-black/20">
              <div className="flex items-center gap-2 border-b bg-muted/50 px-4 py-3">
                <div className="flex gap-1.5">
                  <div className="size-3 rounded-full bg-destructive/80" />
                  <div className="size-3 rounded-full bg-warning/80" />
                  <div className="size-3 rounded-full bg-success/80" />
                </div>
                <span className="ml-2 text-xs text-muted-foreground">
                  github.com / your-org / your-repo / issues
                  / 42
                </span>
              </div>
              <img
                src={githubIssueExample}
                alt="Real GitHub issue created by vivus — showing alert summary, pod logs, context, and @claude mention for AI analysis"
                className="w-full"
                loading="lazy"
              />
            </div>
          </div>
        </FadeContent>

        <div className="mt-10 flex flex-wrap items-center justify-center gap-6 text-sm text-muted-foreground">
          <span className="flex items-center gap-1.5">
            <IconServer className="size-4" />
            Auto-detected crash loop
          </span>
          <span className="flex items-center gap-1.5">
            <IconBrandGithub className="size-4" />
            Issue created in 60 seconds
          </span>
          <span className="flex items-center gap-1.5">
            <IconRobot className="size-4" />
            @claude mention included
          </span>
        </div>

        <div className="mt-10 text-center">
          {env.VITE_WAITLIST_MODE ? (
            <Button
              render={<Link to="/waitlist" />}
              size="lg"
              variant="outline"
            >
              Get Early Access
              <MoveRight className="ml-2" strokeWidth={1} />
            </Button>
          ) : (
            <Button
              render={<Link to="/app/dashboard" />}
              size="lg"
              variant="outline"
            >
              Get Started
              <MoveRight className="ml-2" strokeWidth={1} />
            </Button>
          )}
        </div>
      </div>
    </section>
  );
}

function IntegrationsSection() {
  const integrations = [
    {
      name: "Kubernetes",
      description:
        "Read-only cluster access with least-privilege RBAC. No exec, no secrets access. Auto-refreshing tokens.",
      icon: <KubernetesLogo className="size-12" />,
    },
    {
      name: "Sentry",
      description:
        "Monitor new errors and regressions. Auto-close GitHub issues when Sentry issues are resolved.",
      icon: <SentryLogo className="size-12" />,
    },
    {
      name: "Azure DevOps",
      description:
        "Monitor CI/CD pipeline failures on protected branches. Get detailed error logs and task failure analysis.",
      icon: (
        <svg viewBox="0 0 24 24" className="size-12">
          <path
            className="fill-azure"
            d="M0 8.877L2.247 5.91l8.405-3.416V.022l7.37 5.393L2.966 8.338v8.225L0 15.707zm24-4.45v14.651l-5.753 4.9-9.303-3.057v3.056l-5.978-7.416 15.057 1.798V5.415z"
          />
        </svg>
      ),
    },
    {
      name: "OpenTelemetry",
      description:
        "Push errors from any OTLP-instrumented service. Error logs and failed traces automatically become GitHub issues.",
      icon: (
        <IconSatellite className="size-12 text-brand-teal-foreground" />
      ),
    },
    {
      name: "GitHub Actions",
      description:
        "Monitor workflow failures on protected branches. Auto-creates issues with job logs and error details.",
      icon: (
        <IconBrandGithub className="size-12 text-success-foreground" />
      ),
    },
    {
      name: "GitHub Issues",
      description:
        "Issues created in your repository. Your AI agent (Claude Code, Copilot) analyzes using your own subscription.",
      icon: (
        <svg viewBox="0 0 24 24" className="size-12">
          <path
            fill="currentColor"
            d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"
          />
        </svg>
      ),
    },
  ];

  return (
    <section className="border-t py-16">
      <div className="container mx-auto max-w-6xl px-4 md:px-6">
        <h2 className="mb-4 text-center text-2xl font-semibold">
          Works with your existing stack
        </h2>
        <p className="mx-auto mb-12 max-w-2xl text-center text-muted-foreground">
          Kubernetes, Azure DevOps, Sentry, OpenTelemetry,
          GitHub Actions, GitHub—one platform, six
          integrations.
        </p>

        <div className="mx-auto grid max-w-6xl gap-8 md:grid-cols-2 lg:grid-cols-3">
          {integrations.map((integration) => (
            <Card
              key={integration.name}
              className="text-center"
            >
              <CardContent variant="pt8">
                <div className="mb-4 flex justify-center">
                  {integration.icon}
                </div>
                <h3 className="mb-2 text-lg font-semibold">
                  {integration.name}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {integration.description}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
}

function HowItWorksSection() {
  const setupSteps = [
    {
      number: "1",
      title: "Connect your infrastructure",
      description:
        "Add your Kubernetes cluster, Azure DevOps, Sentry, or point your OpenTelemetry Collector. Read-only credentials—vivus never writes to your systems.",
      accent: (
        <div className="mt-3 flex items-center gap-3">
          <KubernetesLogo className="size-5" />
          <IconBrandAzure className="size-5 text-azure" />
          <SentryLogo className="size-5" />
          <IconSatellite className="size-5 text-brand-teal-foreground" />
          <IconBrandGithub className="size-5 text-success-foreground" />
        </div>
      ),
    },
    {
      number: "2",
      title: "Install the GitHub App",
      description:
        "One-click install. vivus gets permission to create issues in your repository—nothing else.",
      accent: (
        <div className="mt-3">
          <IconBrandGithub className="size-5 text-muted-foreground" />
        </div>
      ),
    },
    {
      number: "3",
      title: "Create your first agent",
      description:
        "Pick what to monitor, set thresholds, choose a schedule. From every minute to daily—you decide.",
      accent: (
        <div className="mt-3 flex items-center gap-2">
          <span className="rounded bg-muted px-2 py-0.5 font-mono text-xs">
            Every 5 min
          </span>
          <span className="rounded bg-muted px-2 py-0.5 font-mono text-xs">
            Hourly
          </span>
          <span className="rounded bg-muted px-2 py-0.5 font-mono text-xs">
            Daily
          </span>
        </div>
      ),
    },
    {
      number: "4",
      title: "That's it. Agents run automatically.",
      description:
        "No deployment needed. Issues appear in your GitHub repo within minutes. Your AI agent takes it from there.",
      accent: (
        <div className="mt-3">
          <span className="inline-flex items-center gap-1.5 text-xs font-medium text-success-foreground">
            <div className="size-2 animate-pulse rounded-full bg-success" />
            Agents active
          </span>
        </div>
      ),
    },
  ];

  const cycleSteps = [
    {
      step: "01",
      icon: (
        <IconCalendar className="size-6 text-info-foreground" />
      ),
      title: "Agent triggers",
      subtitle: "On your schedule",
      color: "bg-info/10",
    },
    {
      step: "02",
      icon: (
        <IconServer className="size-6 text-brand-purple-foreground" />
      ),
      title: "Collects metrics",
      subtitle: "K8s, Azure, Sentry, OTel",
      color: "bg-brand-purple/10",
    },
    {
      step: "03",
      icon: (
        <IconBell className="size-6 text-destructive" />
      ),
      title: "Detects issues",
      subtitle: "Crashes, failures, errors",
      color: "bg-destructive/10",
    },
    {
      step: "04",
      icon: (
        <IconShield className="size-6 text-warning-foreground" />
      ),
      title: "Deduplicates",
      subtitle: "100 crashes = 1 issue",
      color: "bg-warning/10",
    },
    {
      step: "05",
      icon: (
        <IconBrandGithub className="size-6 text-neutral-foreground" />
      ),
      title: "Creates GitHub issue",
      subtitle: "With logs and @claude",
      color: "bg-neutral/10",
    },
    {
      step: "06",
      icon: (
        <IconRefresh className="size-6 text-success-foreground" />
      ),
      title: "Auto-closes when resolved",
      subtitle: "Reopens if it recurs",
      color: "bg-success/10",
    },
  ];

  return (
    <section className="border-t bg-muted/30 py-16">
      <div className="container mx-auto max-w-6xl px-4 md:px-6">
        <h2 className="mb-4 text-center text-2xl font-semibold">
          How it works
        </h2>
        <p className="mx-auto mb-12 max-w-2xl text-center text-muted-foreground">
          Set up in 5 minutes. Agents run continuously—no
          manual triage needed.
        </p>

        <h3 className="mb-8 text-center text-lg font-semibold text-muted-foreground">
          Setup
        </h3>
        <div className="mx-auto max-w-2xl">
          {setupSteps.map((step, index) => (
            <FadeContent
              key={step.number}
              delay={index * 150}
              duration={600}
              blur
            >
              <div className="flex gap-4 md:gap-6">
                <div className="flex flex-col items-center">
                  <div className="inline-flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-primary text-sm font-bold text-primary-foreground">
                    {step.number}
                  </div>
                  {index < setupSteps.length - 1 && (
                    <div className="my-2 w-px flex-1 bg-border" />
                  )}
                </div>
                <div className="pb-8">
                  <h3 className="mb-1 font-semibold">
                    {step.title}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {step.description}
                  </p>
                  {step.accent}
                </div>
              </div>
            </FadeContent>
          ))}
        </div>

        <div className="my-12 border-t" />

        <h3 className="mb-8 text-center text-lg font-semibold text-muted-foreground">
          Every monitoring cycle
        </h3>
        <div className="mx-auto grid max-w-4xl grid-cols-2 gap-4 md:grid-cols-3">
          {cycleSteps.map((step, index) => (
            <FadeContent
              key={step.step}
              delay={index * 100}
              duration={500}
              blur
            >
              <div className="relative rounded-xl border bg-background p-4 text-center">
                <span className="absolute top-3 left-3 font-mono text-2xs text-muted-foreground/60">
                  {step.step}
                </span>
                <div
                  className={`inline-flex items-center justify-center rounded-xl p-3 ${step.color} mb-3`}
                >
                  {step.icon}
                </div>
                <h3 className="mb-0.5 text-sm font-semibold">
                  {step.title}
                </h3>
                <p className="text-xs text-muted-foreground">
                  {step.subtitle}
                </p>
              </div>
            </FadeContent>
          ))}
        </div>
      </div>
    </section>
  );
}

function FeaturesSection() {
  const features = [
    {
      icon: <IconShield className="size-6 text-primary" />,
      title: "Read-Only Access",
      description:
        "No exec, no secrets, no write operations. Your cluster stays safe.",
    },
    {
      icon: <IconCode className="size-6 text-primary" />,
      title: "Zero Lock-in",
      description:
        "Works with your existing GitHub, Azure, Sentry—and any OpenTelemetry source. Open standards, no proprietary agents.",
    },
    {
      icon: <IconBell className="size-6 text-primary" />,
      title: "Smart Deduplication",
      description:
        "100 pod crashes = 1 issue. No inbox flooding.",
    },
    {
      icon: (
        <IconCalendar className="size-6 text-primary" />
      ),
      title: "24/7 Monitoring",
      description:
        "Scheduled runs every minute to daily. You configure, we watch.",
    },
    {
      icon: (
        <IconBrandGithub className="size-6 text-primary" />
      ),
      title: "Issues, Not Dashboards",
      description:
        "No more staring at graphs. Problems become GitHub issues with context.",
    },
    {
      icon: <IconRobot className="size-6 text-primary" />,
      title: "Your AI, Your Data",
      description:
        "Issues go to your repo. Your AI agent runs on your subscription. Choose which bots get mentioned with an organization catalog, installation defaults, and per-repo inherit, override, or disable controls.",
    },
  ];

  return (
    <section className="border-t py-16">
      <div className="container mx-auto max-w-6xl px-4 md:px-6">
        <h2 className="mb-4 text-center text-2xl font-semibold">
          Why Teams Trust vivus
        </h2>
        <p className="mx-auto mb-12 max-w-2xl text-center text-muted-foreground">
          Read-only cluster access. Issues in your GitHub.
          Your AI, your subscription.
        </p>

        <div className="mx-auto grid max-w-5xl gap-6 md:grid-cols-2 lg:grid-cols-3">
          {features.map((feature) => (
            <Card key={feature.title} size="sm">
              <CardContent>
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 rounded-lg bg-primary/10 p-2">
                    {feature.icon}
                  </div>
                  <div>
                    <h3 className="mb-1 font-semibold">
                      {feature.title}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {feature.description}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
}

function FAQSection() {
  const faqs = [
    {
      question: "Is my cluster data safe?",
      answer:
        "vivus uses read-only Kubernetes access. No exec, no secrets access, no write operations. We never store your logs—only issue metadata.",
    },
    {
      question: "How long does setup take?",
      answer:
        "Under 5 minutes. Connect your K8s cluster, Azure DevOps, Sentry, GitHub Actions, or OpenTelemetry Collector and GitHub—vivus handles the rest.",
    },
    {
      question: "What about alert spam?",
      answer:
        "Fingerprint-based deduplication groups related issues. 100 pod crashes = 1 GitHub issue, not 100 notifications.",
    },
    {
      question: "How does the AI integration work?",
      answer:
        "vivus creates issues in YOUR GitHub repository with @claude mention. Your own Claude Code or Copilot (on your subscription) analyzes and suggests fixes. We don't run AI—your organization's AI agent does.",
    },
    {
      question: "Which branches are monitored?",
      answer:
        "Protected branches: main, master, test, develop, staging, production. PR branches are excluded to reduce noise.",
    },
    {
      question: "What about false positives?",
      answer:
        "vivus uses a 2-minute grace period for transient pod states (startup, rolling deploys) and fingerprint deduplication to group related events. You can also suppress specific namespaces or issue types via vivus.json config.",
    },
    {
      question: "How does OpenTelemetry integration work?",
      answer:
        "Point your OpenTelemetry Collector at vivus's OTLP HTTP endpoint. Error logs (severity ≥ ERROR) and failed traces are automatically extracted, grouped by fingerprint, and turned into GitHub issues. No SDK changes needed—just a Collector config update.",
    },
    {
      question:
        "We already use Datadog/PagerDuty. Why add vivus?",
      answer:
        "vivus doesn't replace your monitoring stack—it bridges the gap between detection and resolution. Datadog alerts you. PagerDuty pages you. vivus creates a GitHub issue with full context so your AI agent (or your team) can start fixing immediately.",
    },
  ];

  return (
    <section className="border-t py-16">
      <div className="container mx-auto max-w-6xl px-4 md:px-6">
        <h2 className="mb-4 text-center text-2xl font-semibold">
          Common questions from DevOps teams
        </h2>
        <p className="mx-auto mb-12 max-w-2xl text-center text-muted-foreground">
          Real answers about security, setup, and how vivus
          fits your workflow.
        </p>

        <div className="mx-auto grid max-w-4xl gap-6 md:grid-cols-2">
          {faqs.map((faq) => (
            <Card key={faq.question} size="sm">
              <CardContent>
                <h3 className="mb-2 font-semibold">
                  {faq.question}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {faq.answer}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
}

function UrgencyCTASection() {
  return (
    <section className="border-t bg-muted/30 py-16">
      <div className="container mx-auto max-w-6xl px-4 md:px-6">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="mb-4 text-2xl font-semibold">
            Join the Beta
          </h2>
          <p className="mb-8 text-muted-foreground">
            Be among the first teams to catch infrastructure
            and application errors before users report them.
            Public launch {getNextLaunchQuarter()}—early
            adopters lock in special pricing forever.
          </p>

          {env.VITE_WAITLIST_MODE ? (
            <Button
              render={<Link to="/waitlist" />}
              size="lg"
            >
              Get Early Access
              <MoveRight className="ml-2" strokeWidth={1} />
            </Button>
          ) : (
            <Button
              render={<Link to="/app/dashboard" />}
              size="lg"
            >
              Get Started
              <MoveRight className="ml-2" strokeWidth={1} />
            </Button>
          )}

          <div className="mt-6 flex flex-wrap items-center justify-center gap-6 text-sm text-muted-foreground">
            <span className="flex items-center gap-1.5">
              <IconShield className="size-4" />
              Free while in beta
            </span>
            <span className="flex items-center gap-1.5">
              <IconCode className="size-4" />
              No credit card
            </span>
            <span className="flex items-center gap-1.5">
              <IconCalendar className="size-4" />
              Setup in 5 minutes
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}

export {
  HeroSection,
  BeforeAfterSection,
  GitHubIssueShowcaseSection,
  HowItWorksSection,
  IntegrationsSection,
  FeaturesSection,
  FAQSection,
  UrgencyCTASection,
};
